import { Tag, X } from 'lucide-react';
import { memo } from 'react';
import { useTranslation } from "react-i18next";

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { contactsLabelsSelectOneObjectByQuery, labelSelectOneObjectByQuery } from "@common-reducers/DBServiceThunks";
import { toggleChatLabelThunk } from '@common-reducers/labels-reducer/labels-thunks';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { namespace } from "@common-config/constants";
import { shouldShowRemoveFromLabel } from './remove-from-label-button.logic';

type Props = {
    waChatId: string,
    size?: number
}

/**
 * One-click "remove this contact from the label you're currently viewing".
 *
 * Fixes the #162 discoverability gap: removing a contact from a label previously
 * required opening the Tag dropdown and un-checking the label (labels are a
 * many-to-many join with no "move" op), so customers resorted to deleting the
 * whole label. This surfaces a direct remove ONLY while a real user label is the
 * active view AND the contact actually belongs to it. Persistence goes through
 * `toggleChatLabelThunk`, which deletes the ContactLabel row (the correct path).
 */
export default memo(function RemoveFromLabelButton({ waChatId, size }: Props) {
    const { t } = useTranslation(namespace);
    const dispatch = useAppDispatch();

    const activeLabelId = useAppSelector(state => state.LabelReducer.activeLabelId);

    const activeLabel = useAppSelector(state =>
        activeLabelId
            ? labelSelectOneObjectByQuery(state.DBLabelReducer, { _id: activeLabelId })
            : undefined
    );

    const contactLabel = useAppSelector(state =>
        activeLabelId
            ? contactsLabelsSelectOneObjectByQuery(state.DBContactsLabelsReducer, {
                labelId: activeLabelId,
                waChatId,
            })
            : undefined
    );

    // Only meaningful when viewing a real user label the contact is actually in.
    if (!shouldShowRemoveFromLabel({ activeLabelId, activeLabel, contactLabel })) {
        return null;
    }

    const labelName = activeLabel?.name ?? '';

    function removeFromLabel(e: React.MouseEvent) {
        e.preventDefault();
        e.stopPropagation();
        dispatch(toggleChatLabelThunk({
            waChatId,
            labelId: activeLabelId,
        }));
    }

    return (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger asChild>
                    <button
                        className="group p-1 rounded transition-colors cursor-pointer text-gray-500 hover:text-red-500"
                        onClick={removeFromLabel}
                    >
                        <Tag size={size || 18} className="currentColor group-hover:hidden" strokeWidth={2} />
                        <X size={size || 18} className="currentColor hidden group-hover:block" strokeWidth={2} />
                    </button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                    <p>{t('labels.removeFromLabel', { defaultValue: 'Remove from "{{label}}"', label: labelName })}</p>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
})
